import { useState, useEffect, useRef } from "react";
import "./WellCompFaceplate.css";
import Swal from "sweetalert2";

const componentsList = [
  "C5+ liquid hydrocarbons (oil)",
  "Dissolved light hydrocarbons C1–C4",
  "Methane (CH4)",
  "Ethane (C2H6)",
  "Propane (C3H8)",
  "Butanes (i-C4 / n-C4)",
  "Pentanes+ (C5+)",
  "Carbon dioxide (CO2)",
  "Nitrogen (N2)",
  "Hydrogen sulfide (H2S)",
  "Formation water (H2O)",
  "Dissolved salts",
  "Wax / paraffins",
  "Asphaltenes",
  "Sand",
  "Organic acids",
  "Trace metals",
  "Scale-forming ions"
];


function WellCompFaceplate({
  title,
  close,
  data,
  updateData,
  defaultPosition = { x: 300, y: 120 }
}) {

  /* ================= DRAG ================= */
  const [position, setPosition] = useState(defaultPosition);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  /* ================= STATE ================= */
  const [percentages, setPercentages] = useState({});
  const [discovered, setDiscovered] = useState("");
  const [pressure, setPressure] = useState("");
  const [temperature, setTemperature] = useState("");
  const [productionRate, setProductionRate] = useState(""); // ✅ FIXED

  const [initialized, setInitialized] = useState(false);

  const totalProduction = 10000;
  const availableReserve = (Number(discovered) || 0) - totalProduction;

  const hasShownWelcomePopup = useRef(false);


  const demoWellData1 = {
    percentages: {
      "C5+ liquid hydrocarbons (oil)": 48,
      "Dissolved light hydrocarbons C1–C4": 3.99,
      "Methane (CH4)": 26.7,
      "Ethane (C2H6)": 5,
      "Propane (C3H8)": 3,
      "Butanes (i-C4 / n-C4)": 2,
      "Pentanes+ (C5+)": 0.5,
      "Carbon dioxide (CO2)": 1.5,
      "Nitrogen (N2)": 0.1,
      "Hydrogen sulfide (H2S)": 0.1,
      "Formation water (H2O)": 8,
      "Dissolved salts": 0.3,
      "Wax / paraffins": 0.4,
      "Asphaltenes": 0.2,
      "Sand": 0.1,
      "Organic acids": 0.05,
      "Trace metals": 0.01,
      "Scale-forming ions": 0.05
    },
    discovered: 729490252, // 5 million BOE
    pressure: 50, // MPa
    temperature: 220, // °C
    productionRate: 150 // BOE/M
  };


  const demoWellData2 = {
    percentages: {
      "C5+ liquid hydrocarbons (oil)": 70,
      "Dissolved light hydrocarbons C1–C4": 5,
      "Methane (CH4)": 6,
      "Ethane (C2H6)": 2,
      "Propane (C3H8)": 1,
      "Butanes (i-C4 / n-C4)": 0.7,
      "Pentanes+ (C5+)": 0.3,
      "Carbon dioxide (CO2)": 2,
      "Nitrogen (N2)": 0.4,
      "Hydrogen sulfide (H2S)": 0.15,
      "Formation water (H2O)": 11,
      "Dissolved salts": 0.4,
      "Wax / paraffins": 0.4,
      "Asphaltenes": 0.3,
      "Sand": 0.1,
      "Organic acids": 0.05,
      "Trace metals": 0.1,
      "Scale-forming ions": 0.1
    },
    discovered: 729787986, // 729.79 million BOE
    pressure: 52, // MPa
    temperature: 220, // °C
    productionRate: 100 // BOE/M
  };


  const demoWellData3 = {
    percentages: {
      "C5+ liquid hydrocarbons (oil)": 1.8,
      "Dissolved light hydrocarbons C1–C4": 0.5,
      "Methane (CH4)": 71.68,
      "Ethane (C2H6)": 10,
      "Propane (C3H8)": 4,
      "Butanes (i-C4 / n-C4)": 2.3,
      "Pentanes+ (C5+)": 1,
      "Carbon dioxide (CO2)": 1.3,
      "Nitrogen (N2)": 0.6,
      "Hydrogen sulfide (H2S)": 0.08,
      "Formation water (H2O)": 6,
      "Dissolved salts": 0.2,
      "Wax / paraffins": 0.4,
      "Asphaltenes": 0.01,
      "Sand": 0.05,
      "Organic acids": 0.05,
      "Trace metals": 0.01,
      "Scale-forming ions": 0.02
    },
    discovered: 729713013, // 5 million BOE
    pressure: 55, // MPa
    temperature: 250, // °C
    productionRate: 120 // BOE/M
  };


  console.log("title", title)

  if (title === "NK-1 WELLHEAD") {
    var demoWellData = demoWellData1;
  } else if (title === "NK-2 WELLHEAD") {
    var demoWellData = demoWellData2;
  } else if (title === "NK-3 WELLHEAD") {
    var demoWellData = demoWellData3;
  }



  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    if (!initialized && data) {
      // Check if this is a new well (no data)
      const hasNoData = !data.percentages || Object.keys(data.percentages).length === 0;

      if (hasNoData && !hasShownWelcomePopup.current) {
        hasShownWelcomePopup.current = true;

        Swal.fire({
          title: 'Welcome to the Well Composition Faceplate!',
          html: `
          <div style="text-align: left; margin-top: 12px;">
            <p style="font-size: 1rem; color: #4b5563; margin-bottom: 16px;">
              This is a new well composition. Would you like to use a demo composition to get started?
            </p>
            <p style="font-size: 0.9rem; color: #6b7280; margin-top: 12px;">
              The demo includes realistic values for oil, gas, and impurities that you can modify later.
            </p>
          </div>
        `,
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Use Demo',
          cancelButtonText: 'Start Empty',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            popup: 'rounded-xl shadow-lg',
            confirmButton: 'font-semibold px-4 py-2',
            cancelButton: 'font-semibold px-4 py-2'
          }
        }).then((result) => {
          if (result.isConfirmed) {
            // Use demo data
            setPercentages(demoWellData.percentages);
            setDiscovered(demoWellData.discovered.toString());
            setPressure(demoWellData.pressure.toString());
            setTemperature(demoWellData.temperature.toString());
            setProductionRate(demoWellData.productionRate.toString());
          }
          // If declined, do nothing - keep empty state
          setInitialized(true);
        });
      } else {
        // Load existing data
        setPercentages(data.percentages || {});
        setDiscovered(data.discovered || "");
        setPressure(data.pressure || "");
        setTemperature(data.temperature || "");
        setProductionRate(data.productionRate || "");
        setInitialized(true);
      }
    }
  }, [data, initialized]);



  /* ================= DRAG ================= */
  const handleMouseDown = (e) => {
    setDragging(true);
    setOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging) return;
      setPosition({
        x: e.clientX - offset.x,
        y: e.clientY - offset.y
      });
    };

    const stop = () => setDragging(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
    };
  }, [dragging, offset]);

  /* ================= UPDATE PARENT ================= */
  useEffect(() => {
    // Normalize percentages
    const normalizedPercentages = { ...percentages };

    const total = Object.values(normalizedPercentages).reduce(
      (sum, val) => sum + (Number(val) || 0),
      0
    );

    if (total > 0) {
      Object.keys(normalizedPercentages).forEach((key) => {
        normalizedPercentages[key] =
          ((Number(normalizedPercentages[key]) || 0) / total) * 100;
      });
    }

    updateData(title, {
      percentages: normalizedPercentages,
      discovered,
      pressure,
      temperature,
      productionRate,

      pressureInput: Number(pressure) || 0,
      temperatureInput: Number(temperature) || 0,
      productionRateInput: Number(productionRate) || 0
    });

  }, [percentages, discovered, pressure, temperature, productionRate]);

  /* ================= INPUT ================= */
  const handleChange = (comp, value) => {
    setPercentages(prev => ({
      ...prev,
      [comp]: value
    }));
  };

  /* ================= CALC ================= */
  const getFraction = (comp) => {
    const percent = Number(percentages[comp]) || 0;
    const reserve = Number(availableReserve) || 0;

    return (percent / 100) * reserve;
  };

  const totalPercent = Object.values(percentages).reduce(
    (sum, val) => sum + (Number(val) || 0),
    0
  );

  const totalFraction = componentsList.reduce(
    (sum, c) => sum + getFraction(c),
    0
  );

  /* ================= GROUP DEFINITIONS ================= */

  const oilGroup = [
    "C5+ liquid hydrocarbons (oil)",
    "Pentanes+ (C5+)",
    "Wax / paraffins",
    "Asphaltenes"
  ];

  const gasGroup = [
    "Dissolved light hydrocarbons C1–C4",
    "Methane (CH4)",
    "Ethane (C2H6)",
    "Propane (C3H8)",
    "Butanes (i-C4 / n-C4)"
  ];

  const impGroup = [
    "Carbon dioxide (CO2)",
    "Nitrogen (N2)",
    "Hydrogen sulfide (H2S)",
    "Formation water (H2O)",
    "Dissolved salts",
    "Sand",
    "Organic acids",
    "Trace metals",
    "Scale-forming ions"
  ];




  /* ================= GROUP SUM FUNCTION ================= */
  const sumGroup = (group, type = "percent") => {
    return group.reduce((sum, comp) => {
      const percent = Number(percentages[comp]) || 0;
      const frac = getFraction(comp);

      return sum + (type === "percent" ? percent : frac);
    }, 0);
  };








  /* ================= RENDER ================= */
  return (
    <div
      className="faceplate-container"
      style={{ left: position.x, top: position.y + 20 }}
    >

      {/* HEADER */}
      <div className="faceplate-header" onMouseDown={handleMouseDown}>
        <h2>{title}</h2>
        {/* <button className="close-btn" onClick={close}>
          &times;
        </button> */}
        <button
          className="demo-btn"
          onClick={() => {
            Swal.fire({
              title: 'Load Demo Composition?',
              text: 'This will replace all current data with the demo composition. Are you sure?',
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Yes, load demo',
              cancelButtonText: 'Cancel'
            }).then((result) => {
              if (result.isConfirmed) {
                setPercentages(demoWellData.percentages);
                setDiscovered(demoWellData.discovered.toString());
                setPressure(demoWellData.pressure.toString());
                setTemperature(demoWellData.temperature.toString());
                setProductionRate(demoWellData.productionRate.toString());

                Swal.fire('Demo Loaded!', 'The demo composition has been loaded.', 'success');
              }
            });
          }}
        >
          Load Demo
        </button>
      </div>


      {/* TABLE */}
      <div className="table">

        

        <div className="table-header">
          <span>Components</span>
          <span>%</span>
          <span>Fraction</span>
        </div>

        {componentsList.map((comp) => (
          <div className="table-row" key={comp}>
            <span>{comp}</span>

            <input
              type="number"
              step="any"
              className="percent-input"
              value={percentages[comp] || ""}
              onChange={(e) => handleChange(comp, e.target.value)}
            />

            <span className="fraction-output">
              {getFraction(comp).toFixed(4)}
            </span>
          </div>
        ))}

        <div className="table-row total-row">
          <span>TOTAL</span>
          <span>{totalPercent.toFixed(4)}</span>
          <span>{totalFraction.toFixed(4)}</span>
        </div>
      </div>

      {/* SUMMARY */}
      <div className="summary">

        <div className="total-oil">
          <h4>TOTAL OIL</h4>
          <p>%: {sumGroup(oilGroup, "percent").toFixed(2)}</p>
          <p>Frac: {sumGroup(oilGroup, "fraction").toFixed(2)}</p>
        </div>

        <div className="total-gas">
          <h4>TOTAL GAS</h4>
          <p>%: {sumGroup(gasGroup, "percent").toFixed(2)}</p>
          <p>Frac: {sumGroup(gasGroup, "fraction").toFixed(2)}</p>
        </div>

        <div className="impurities-water">
          <h4>IMPURITIES & WATER</h4>
          <p>%: {sumGroup(impGroup, "percent").toFixed(2)}</p>
          <p>Frac: {sumGroup(impGroup, "fraction").toFixed(2)}</p>
        </div>

      </div>

      {/* PRESSURE & TEMP */}
      <div className="pt-section">
        <label>Reservoir Pressure</label>
        <input
          value={pressure}
          onChange={(e) => setPressure(e.target.value)}
        />
        <span>MPa</span>

        <label>Reservoir Temperature</label>
        <input
          value={temperature}
          onChange={(e) => setTemperature(e.target.value)}
        />
        <span>°C</span>

        <label>Production Rate</label>
        <input
          value={productionRate}
          onChange={(e) => setProductionRate(e.target.value)} // ✅ FIXED
        />
        <span>BOE/M</span>
      </div>

      {/* RESERVES */}
      <div className="reserve-section">

        <label>Discovered Reserves</label>
        <input
          value={discovered}
          onChange={(e) => setDiscovered(e.target.value)}
        />
        <span>BOE</span>

        <label>TOTAL PRODUCTION</label>
        <input
          value={totalProduction}
          readOnly
          className="output-field"
        />
        <span>BOE</span>

        <label>Available Reserves</label>
        <input
          value={availableReserve}
          readOnly
          className="output-field"
        />
        <span>BOE</span>

      </div>

      <button
        style={{ position: "absolute", left: 0, top: -10 }}
        className="close-btn"
        onClick={close}
      >
        Close
      </button>

    </div>
  );
}

export default WellCompFaceplate;